'use client'

import { selectPrimaryRecommendation } from '@/lib/app-categories/discontinued'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import AppIcon from '@/components/content/app/AppIcon'
import { type CriterionWithRating } from '@/components/ui/CriteriaOverview'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import RichText from '@/components/content/common/RichText'
import RecommendationBadge from '@/components/ui/RecommendationBadge'
import Menu from '@/components/ui/Menu'
import Rating from '@/components/ui/Rating'
import Tooltip from '@/components/ui/Tooltip'
import Loader from '@/components/ui/Loader'
import Badge from '@/components/ui/Badge'
import ExternalIndicator from '@/components/ui/ExternalIndicator'
import PrimaryButton from '@/components/content/app/PrimaryButton'
import { Icon } from '@/icons/Icon'
import { generateMenu, type MenuItem } from '@/helpers/menus'
import { cn, getBrandColors, trimUrl, isPopulatedObject } from '@/lib/utils'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { queryPayload } from '@/lib/payloadClient'
import {
  determineDealsToDisplay,
  formatDeal,
  formatDealButton,
} from '@/components/content/deals/helpers'
import { determinePremiumPartner, getPrimaryButton } from '@/components/content/app/helpers'
import { useOptionalCompareSlug } from '@/components/content/common/CompareSlugContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { extractUniqueCriteriaFromApp, processAffiliateLink } from '@/lib/formatting'
import { type FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'
import type { RankProps } from './AppRanking'
import type { Block } from '@/types/app'
import type {
  Deal,
  AppCategoryRecommendation,
  App,
  CategoryCriteriaCriterion,
  Category,
  ReviewCategoryCriterionRating,
} from '@/payload-types'

const logger = createLogger('AppInfo')

type CompareWithApp = Pick<App, 'id' | 'slug'>

type AppInfoProps = {
  app: Pick<
    App,
    | 'id'
    | 'name'
    | 'slug'
    | 'icon'
    | 'glyph'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'homepageImage'
    | 'affiliateLink'
    | 'categoryRecommendations'
    | 'partnershipTier'
    | 'websiteUrl'
  >
  recommendation?: Recommendation
  bestFor?: string
  description?: string
  responsive?: boolean
  tldr?: Block[]
  compareWithApp?: CompareWithApp
  theme?: 'light' | 'dark'
  tag?: 'h2' | 'h3' | 'span'
  position?: 'down' | 'up'
  hideRating?: boolean
  category?: Category
  verdict?: boolean
  summary?: boolean
  rank?: RankProps
  categoryRank?: number
  swap?: boolean
  flag?: string[]
  serverRatings?: Pick<
    ReviewCategoryCriterionRating,
    'rating' | 'isApplicable' | 'categoryCriterion'
  >[]
  serverDeals?: Deal[]
  // Whole-card click target, two variants by caller kind:
  // - onClickAction (function): CLIENT callers (AppRanking) that need richer behavior
  //   than navigation — analytics + in-page scroll.
  // - navigateHref (URL string): SERVER-component callers (AppOverview), which can't
  //   pass a function across the server→client boundary, so AppInfo runs router.push
  //   itself. The overlay prefers onClickAction, falling back to navigateHref.
  onClickAction?: () => void
  navigateHref?: string
  featuredPlacement?: FeaturedPlacement
  showCategoryBadge?: boolean
  inlineButtons?: boolean
  isWinner?: boolean
}

export function AppInfo({
  app,
  recommendation,
  bestFor,
  description,
  responsive,
  tldr,
  compareWithApp,
  theme,
  tag = 'h2',
  position = 'down',
  hideRating = false,
  category,
  verdict = false,
  summary = false,
  rank,
  categoryRank,
  swap = false,
  flag,
  serverRatings,
  serverDeals,
  onClickAction,
  navigateHref,
  featuredPlacement,
  showCategoryBadge = false,
  inlineButtons = false,
  isWinner = false,
}: AppInfoProps) {
  const router = useRouter()

  const [efficiencyPoints, setEfficiencyPoints] = useState(() => {
    if (!serverRatings) return { points: 0, total: 0 }
    const applicable = serverRatings.filter((r) => r.isApplicable !== false)
    const points = applicable.reduce((acc, r) => (r.rating && r.rating >= 5 ? acc + 1 : acc), 0)
    return { points, total: applicable.length }
  })
  // const [efficiencyPointsLoaded, setEfficiencyPointsLoaded] = useState(!!serverRatings)
  const [reviewCriterionRatings, setReviewCriterionRatings] = useState<
    { categoryCriterion?: number | CategoryCriteriaCriterion | null; rating?: number | null }[]
  >(serverRatings ?? [])
  const [hideButton, setHideButton] = useState(false)

  const primaryCategory = useMemo(
    () =>
      selectPrimaryRecommendation(
        (app.categoryRecommendations?.docs ?? []) as AppCategoryRecommendation[],
      )?.category as
        | Pick<Category, 'id' | 'categoryCriteria' | 'name' | 'slug' | 'icon'>
        | undefined,
    [app.categoryRecommendations?.docs],
  )

  const primaryCategoryId = useMemo(
    () => (isPopulatedObject<Pick<Category, 'id'>>(primaryCategory) ? primaryCategory.id : null),
    [primaryCategory],
  )

  // Use explicit categoryId prop when provided (e.g. compare page scoping), otherwise fall back to primary
  const categoryId = category?.id
  const effectiveCategoryId = categoryId ?? primaryCategoryId

  const effectiveCategory = useMemo(() => {
    if (!categoryId || categoryId === primaryCategoryId) return primaryCategory
    const acrs = (app.categoryRecommendations?.docs ?? []) as AppCategoryRecommendation[]
    const match = acrs.find((c) =>
      typeof c.category === 'number'
        ? c.category === categoryId
        : (c.category as Pick<Category, 'id'>)?.id === categoryId,
    )
    return (match?.category as typeof primaryCategory) ?? primaryCategory
  }, [app.categoryRecommendations?.docs, categoryId, primaryCategoryId, primaryCategory])

  const affiliate = processAffiliateLink(app.affiliateLink, app.slug, app.name, app.partnershipTier)
  const isPremiumPartner = determinePremiumPartner(app.partnershipTier)

  useEffect(() => {
    // Skip client-side fetch when server-provided ratings are available (e.g. on /best pages)
    if (serverRatings) return

    async function fetchAndSetEfficiencyPoints() {
      if (!app.id || !effectiveCategoryId) return

      try {
        const { docs: ratingDocs } = await queryPayload({
          collection: 'review_categoryCriterion_rating',
          select: { rating: true, isApplicable: true, categoryCriterion: true },
          where: {
            'review.app': { equals: app.id },
            'review._status': { equals: 'published' },
            'categoryCriterion.category': { equals: effectiveCategoryId },
          },
          depth: 1,
          pagination: false,
          cacheKey: `query:rcr:eff:${app.id}:${effectiveCategoryId}`,
        })

        const applicableDocs = ratingDocs.filter((doc) => doc?.isApplicable !== false)
        const points = applicableDocs.reduce((acc, doc) => {
          if (doc?.rating && doc.rating >= 5) {
            return acc + 1
          }
          return acc
        }, 0)

        setEfficiencyPoints({ points, total: applicableDocs.length })
        setReviewCriterionRatings(ratingDocs)
      } catch (error) {
        logger.error('Failed to fetch efficiency points:', error)
      } finally {
        // setEfficiencyPointsLoaded(true)
      }
    }

    fetchAndSetEfficiencyPoints()
  }, [app.id, effectiveCategoryId, serverRatings])

  const criteriaWithRatings = useMemo(
    () =>
      extractUniqueCriteriaFromApp(app, reviewCriterionRatings, effectiveCategoryId ?? undefined),
    [app, reviewCriterionRatings, effectiveCategoryId],
  )

  const { backgroundColor } = getBrandColors(app)

  const Tag = tag as 'h2' | 'h3' | 'span'

  const trackFeaturedInteraction = useCallback(() => {
    if (featuredPlacement) trackFeaturedClick(featuredPlacement, app.name)
  }, [app.name, featuredPlacement])

  const appName = (
    <Tag
      className={cn(
        'line-clamp-1 font-semibold leading-tight',
        verdict ? 'text-20' : 'text-16 lg:text-17',
        theme === 'dark' ? 'text-white' : 'text-gray-950 dark:text-white',
      )}
    >
      {app.name}
    </Tag>
  )

  const categoryBadgeCategory = showCategoryBadge ? category : primaryCategory
  const showCategoryIconBadge =
    !summary &&
    !verdict &&
    ((showCategoryBadge && Boolean(categoryBadgeCategory?.icon)) ||
      (rank === 'blank' && Boolean(categoryBadgeCategory?.icon)))

  const appBadges = (
    <div className="relative z-20 flex items-center gap-2">
      {showCategoryIconBadge && categoryBadgeCategory?.icon && categoryBadgeCategory.slug && (
        <Tooltip text={categoryBadgeCategory.name}>
          <Link
            href={`/best/${categoryBadgeCategory.slug}`}
            className="flex h-5 w-5 items-center justify-center rounded-key border border-gray-200 bg-white text-zinc-400 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-zinc-950 dark:hover:bg-zinc-800"
          >
            <Icon name={categoryBadgeCategory.icon} size={12} strokeWidth={1} />
          </Link>
        </Tooltip>
      )}

      {recommendation && rank !== 'blank' && (
        <RecommendationBadge
          recommendation={recommendation}
          efficiencyPoints={efficiencyPoints}
          category={effectiveCategory}
        />
      )}
      {!hideRating && (
        <Rating
          app={app}
          efficiencyPoints={efficiencyPoints}
          criteriaWithRatings={criteriaWithRatings}
          verticalAlign={summary ? 'top' : 'center'}
          horizontalAlign={swap ? 'left' : 'right'}
        />
      )}
      {typeof categoryRank === 'number' &&
        (effectiveCategory?.slug ? (
          <Link
            href={`/best/${effectiveCategory.slug}`}
            onClick={() => {
              try {
                localStorage.setItem('efficient:highlightedRank', String(categoryRank))
              } catch (error) {
                logger.warn('Failed to store highlighted rank:', error)
              }
            }}
          >
            <Badge style="outline" className="cursor-pointer">
              #{categoryRank}
            </Badge>
          </Link>
        ) : (
          <Badge style="outline">#{categoryRank}</Badge>
        ))}
      {app.partnershipTier === 'sponsored' && <Badge>Sponsored</Badge>}
    </div>
  )

  const descriptionText = description || bestFor
  const appDescription = descriptionText ? (
    <Tooltip
      text={descriptionText}
      position="down"
      delay={true}
      wrap={true}
      className="cursor-pointer"
    >
      <p
        className={cn(
          'w-full text-14',
          responsive ? 'px-5 text-center md:px-0 md:text-left md:leading-normal' : 'leading-normal',
          theme === 'dark' ? 'text-zinc-400' : 'text-gray-600 dark:text-zinc-400',
          verdict ? 'px-5 text-center md:px-0' : 'md:line-clamp-1',
        )}
      >
        {descriptionText}
      </p>
    </Tooltip>
  ) : null

  return (
    <div
      className={cn(
        'flex w-full items-center',
        !hideButton && 'flex-col',
        summary ? 'md:items-start' : '',
        summary || verdict ? 'gap-6' : 'gap-3 sm:flex-row',
        inlineButtons && 'sm:pr-4',
      )}
    >
      <div
        className={cn(
          'w-full',
          summary || verdict ? 'flex flex-col items-center gap-6 md:items-start' : '',
        )}
      >
        <span
          className={cn(
            'relative flex w-full',
            rank === 'ordered' ? 'items-start sm:items-center' : 'items-center',
            responsive ? 'flex-col items-center gap-6 md:flex-row md:gap-4' : 'gap-4',
            verdict ? 'flex-col items-center gap-6' : '',
          )}
        >
          <AppIcon
            icon={app.icon}
            glyph={app.glyph}
            name={app.name}
            slug={app.slug}
            ignoreScroll={verdict || summary}
            onActivate={trackFeaturedInteraction}
            background={backgroundColor}
            size={48}
            invert={theme === 'dark' ? true : false}
          />
          <div
            className={cn(
              'flex flex-col',
              rank !== 'ordered' ? 'pt-0.5' : 'sm:pt-0.5',
              responsive ? 'items-center gap-2 md:items-start' : 'gap-1',
            )}
          >
            <div
              className={cn(
                'flex items-center',
                verdict ? 'mb-2.5 flex-col gap-3' : 'h-6 gap-2 md:h-5',
              )}
            >
              {verdict ? <Link href={`/apps/${app.slug}`}>{appName}</Link> : appName}
              {appBadges}
            </div>
            {verdict ? (
              <Link href={`/apps/${app.slug}`} className="justify-center">
                {appDescription}
              </Link>
            ) : (
              appDescription
            )}
          </div>

          {!inlineButtons && (onClickAction || navigateHref) && (
            <button
              onClick={() => {
                if (onClickAction) onClickAction()
                else if (navigateHref) router.push(navigateHref)
              }}
              type="button"
              className="absolute left-0 top-0 z-0 h-full w-full cursor-pointer"
              aria-label={`View ${app.name} details`}
            ></button>
          )}
        </span>

        {tldr && (
          <>
            <div className="text-center text-gray-200 md:text-left">
              <RichText content={tldr} />
            </div>

            {isPremiumPartner && (
              <Link
                href={affiliate.url}
                passReferrerUrl={affiliate.passReferrerUrl}
                sponsored={affiliate.isSponsored}
                target="_blank"
                className="group w-fit text-14 text-white underline underline-offset-4 transition-colors hover:text-primary-500"
              >
                {trimUrl(app.websiteUrl)}
                <ExternalIndicator className="ml-0" />
              </Link>
            )}
          </>
        )}
      </div>

      <AppButtons
        app={app}
        category={category}
        theme={theme}
        criteria={criteriaWithRatings}
        compareWithApp={compareWithApp}
        position={position}
        isSummary={summary}
        isBest={recommendation === Recommendation.TopPick}
        isRecommended={
          recommendation !== Recommendation.Capable &&
          recommendation !== Recommendation.NotRecommended
        }
        isVerdict={verdict}
        rank={rank}
        flag={flag}
        serverDeals={serverDeals}
        featuredPlacement={featuredPlacement}
        hideButton={hideButton}
        setHideButtonAction={setHideButton}
        inlineButtons={inlineButtons}
        isWinner={isWinner}
      />
    </div>
  )
}

type AppButtonsProps = {
  app: Pick<App, 'id' | 'slug' | 'name' | 'affiliateLink' | 'partnershipTier'>
  category?: Category
  theme?: 'light' | 'dark'
  criteria?: CriterionWithRating[]
  compareWithApp?: CompareWithApp
  position?: 'down' | 'up'
  isSummary?: boolean
  isBest: boolean
  isRecommended?: boolean
  isVerdict?: boolean
  rank?: RankProps
  flag?: string[]
  serverDeals?: Deal[]
  featuredPlacement?: FeaturedPlacement
  hideButton: boolean
  setHideButtonAction: (value: boolean) => void
  inlineButtons?: boolean
  isWinner?: boolean
}

function buildDealsMenu(deals: Deal[], appSlug: string | undefined): MenuItem[] {
  if (!deals.length) return []
  return deals.map((deal) => {
    const formattedDeal = formatDeal(deal)
    return {
      label: formattedDeal.button,
      icon: formattedDeal.icon,
      link: `/deals/${appSlug}?claim=true`,
      onClick: () => {
        localStorage.setItem('efficient:claim', 'true')
        if (deals.length > 1) {
          localStorage.setItem('efficient:deal_id', deal.id.toString())
        }
      },
    }
  })
}

export function AppButtons({
  app,
  category,
  theme,
  criteria,
  compareWithApp,
  position,
  isSummary,
  isBest,
  isRecommended,
  isVerdict,
  rank,
  flag,
  serverDeals,
  featuredPlacement,
  hideButton,
  setHideButtonAction,
  inlineButtons,
  isWinner,
}: AppButtonsProps) {
  const isBroadCategory = Boolean(category?.isBroadCategory)
  const resolveCompareSlug = useOptionalCompareSlug()
  const canonicalCompareSlug =
    app?.id && compareWithApp?.id ? resolveCompareSlug?.(app.id, compareWithApp.id) : undefined

  const initialDeals = useMemo(() => {
    if (!serverDeals) return null
    const dealsToDisplay = determineDealsToDisplay(serverDeals)
    return {
      dealCount: dealsToDisplay.length,
      menu: generateMenu('app', {
        app,
        compareWithApp,
        canonicalCompareSlug,
        isBroadCategory,
        criteria,
        deals: dealsToDisplay,
      }),
      dealsMenu: buildDealsMenu(dealsToDisplay, app.slug),
      primaryDeal: dealsToDisplay[0],
    }
  }, [serverDeals, app, compareWithApp, canonicalCompareSlug, isBroadCategory, criteria])

  const [dealCount, setDealCount] = useState(initialDeals?.dealCount ?? 0)
  const [menu, setMenu] = useState<MenuItem[]>(initialDeals?.menu ?? [])
  const [menuIsLoaded, setMenuIsLoaded] = useState(!!initialDeals)
  const [dealsMenu, setDealsMenu] = useState<MenuItem[]>(initialDeals?.dealsMenu ?? [])
  const [primaryDeal, setPrimaryDeal] = useState<Deal | undefined>(initialDeals?.primaryDeal)

  const trackFeaturedInteraction = useCallback(() => {
    if (!featuredPlacement) return
    trackFeaturedClick(featuredPlacement, app.name)
  }, [app.name, featuredPlacement])

  const trackedMenu = useMemo(() => {
    if (!featuredPlacement) return menu

    const addTracking = (items: MenuItem[]): MenuItem[] =>
      items.map((item) => {
        const shouldTrackLink = Boolean(item.link) && item.label !== 'Share'
        const existingSimultaneousClick = item.simultaneousClick

        return {
          ...item,
          sub: item.sub ? addTracking(item.sub) : undefined,
          simultaneousClick: shouldTrackLink
            ? () => {
                existingSimultaneousClick?.()
                trackFeaturedInteraction()
              }
            : existingSimultaneousClick,
        }
      })

    return addTracking(menu)
  }, [featuredPlacement, menu, trackFeaturedInteraction])

  useEffect(() => {
    // Skip client-side fetch when server-provided deals are available (e.g. on /best pages)
    if (serverDeals) return

    async function fetchAndSetDeals(appId: App['id']) {
      try {
        const { docs: deals } = await queryPayload({
          collection: 'deal',
          select: {
            name: true,
            unitType: true,
            isReward: true,
            durationDays: true,
            quantity: true,
            order: true,
            featuredOrder: true,
            startDate: true,
            endDate: true,
            product: true,
            promoCode: true,
            desktopIsRequired: true,
          },
          where: { app: { equals: appId } },
          depth: 0,
          // Shape-suffixed cacheKey: this select includes `product` and
          // `desktopIsRequired`, unlike Ad.tsx's. See plan/KNOWN_ISSUES.md §2.
          cacheKey: `query:deal:app:${appId}:withProductDesktop`,
        })

        const dealsToDisplay = determineDealsToDisplay(deals)

        setDealCount(dealsToDisplay.length)
        setMenu(
          generateMenu('app', {
            app,
            compareWithApp,
            canonicalCompareSlug,
            isBroadCategory,
            criteria,
            deals: dealsToDisplay,
          }),
        )
        setDealsMenu(buildDealsMenu(dealsToDisplay, app.slug))
        setPrimaryDeal(dealsToDisplay[0])
      } catch (error) {
        logger.error('Failed to fetch deals in AppButtons:', error)
      } finally {
        setMenuIsLoaded(true)
      }
    }

    if (!app?.id) return
    fetchAndSetDeals(app.id)
  }, [app, compareWithApp, canonicalCompareSlug, isBroadCategory, criteria, serverDeals])

  const handleClick = () => {
    trackFeaturedInteraction()
    if (dealCount > 0) {
      localStorage.setItem('efficient:claim', 'true')
    }
  }

  const isPartner = determinePremiumPartner(app.partnershipTier)
  const isNested = theme === 'light' || flag?.includes('demo')
  const isTrending = flag?.includes('trending')
  const showPrimary = (isBest || isTrending) && (rank === 'ordered' || rank === 'grouped')

  const buttonClass = cn(
    'group order-1 sm:order-2 flex justify-center items-center w-full sm:w-8 h-8 rounded-full border transition-all ease-in-out',
    !inlineButtons && 'sm:rounded-r-none border-r-0 sm:w-9',
    inlineButtons && 'lg:w-auto lg:shrink-0 lg:gap-0',
    showPrimary
      ? 'bg-primary-500 border-primary-500 text-white'
      : theme === 'dark'
        ? 'bg-zinc-800 border-zinc-800 text-zinc-400 sm:hover:bg-zinc-900'
        : 'bg-gray-200 sm:bg-gray-100 border-gray-200 hover:bg-gray-200/50 text-gray-500',
  )
  const tagClass = cn(
    'sm:hidden md:flex md:opacity-0 md:-translate-x-3 md:group-hover:opacity-100 md:border-r md:border-y md:group-hover:translate-x-0 md:absolute md:h-8 md:w-fit md:pl-2 md:pr-3 md:items-center md:justify-center md:rounded-r-full md:left-full text-14 font-semibold md:font-medium whitespace-nowrap transition-all ease-in-out',
    showPrimary
      ? 'text-white border-primary-500 bg-primary-500 md:before:content-[""] md:before:absolute md:before:left-0 md:before:h-full md:before:w-full md:before:bg-gradient-to-r md:before:from-black/20 md:before:opacity-100 md:group-hover:before:opacity-0 md:before:transition-opacity md:before:to-transparent md:before:rounded-r-full'
      : theme === 'dark'
        ? 'bg-zinc-800 sm:bg-zinc-900 text-zinc-300 sm:text-zinc-400 border-zinc-800 sm:group-hover:text-zinc-300'
        : 'bg-gray-200 sm:bg-white text-gray-700 sm:text-gray-500 border-gray-200 sm:group-hover:bg-gray-100 sm:group-hover:text-gray-700',
    isPartner && !isNested && 'md:translate-x-0 md:opacity-100',
  )

  const dealLabel = primaryDeal ? formatDealButton(primaryDeal) : undefined

  const categoryApps =
    (category?.appRecommendations?.docs as AppCategoryRecommendation[])
      ?.map((rec) => {
        const app = rec.app
        return isPopulatedObject<App>(app) ? app.partnershipTier : null
      })
      .filter((tier): tier is NonNullable<typeof tier> => tier !== null) || []

  // lg (not md): tablets (768-1023px) are fallback users for desktop-required
  // deals, so they must not be classified as desktop.
  const { lg } = useBreakpoint()

  const primaryButton = getPrimaryButton({
    app,
    deal: { count: dealCount, label: dealLabel },
    compareWithApp,
    canonicalCompareSlug,
    categoryApps,
    isRecommended,
    isBest,
    isSummary,
    isBroadCategory,
    isDesktop: lg,
    desktopIsRequired: primaryDeal?.desktopIsRequired ?? false,
  })

  useEffect(() => {
    setHideButtonAction(primaryButton.isReadReview && !isSummary && !isVerdict)
  }, [primaryButton.isReadReview, isSummary, isVerdict, setHideButtonAction])

  // Inline lists give only the winner the full pill; other rows keep the compact icon button
  const showInlinePrimary = Boolean(inlineButtons && isPartner && isWinner)

  const secondaryLink = !isBest && !isPartner ? `/apps/${app.slug}` : undefined
  const secondaryCta = !isBest && !isPartner ? 'Read review' : undefined

  if (!menuIsLoaded) {
    return <Loader />
  }

  return (
    <>
      {inlineButtons && (
        <PrimaryButton
          primaryButton={primaryButton}
          primaryDeal={primaryDeal}
          app={app}
          onClick={primaryButton.isDeal ? handleClick : trackFeaturedInteraction}
          className="absolute inset-0 z-0"
          decorative
        />
      )}
      <div
        className={cn(
          'flex',
          isVerdict ? 'relative z-20 mt-auto w-full' : '',
          inlineButtons && 'relative lg:shrink-0',
          isSummary ? 'gap-3' : 'gap-2 sm:w-auto',
          !isSummary && (hideButton ? 'w-auto' : 'w-full'),
        )}
      >
        <div className={isSummary || isVerdict || inlineButtons ? 'order-3' : 'order-3 sm:order-1'}>
          <Menu items={trackedMenu} position={position} />
        </div>

        {isSummary || isVerdict ? (
          <>
            <PrimaryButton
              primaryButton={primaryButton}
              primaryDeal={primaryDeal}
              onClick={trackFeaturedInteraction}
              app={app}
              className={cn('order-1', isVerdict ? 'w-full' : '')}
            >
              <Pill theme={theme} style="primary" className={cn(isVerdict ? 'w-full' : '')}>
                {primaryButton.label}
              </Pill>
            </PrimaryButton>

            {secondaryCta && secondaryLink && (
              <Link href={secondaryLink} onClick={trackFeaturedInteraction} className="order-2">
                <Pill theme={theme} style="outline">
                  {secondaryCta}
                </Pill>
              </Link>
            )}
          </>
        ) : showInlinePrimary ? (
          <>
            <PrimaryButton
              primaryButton={primaryButton}
              primaryDeal={primaryDeal}
              onClick={trackFeaturedInteraction}
              app={app}
              className="order-1 w-full sm:w-auto lg:w-full"
            >
              <Pill
                theme={theme}
                style="primary"
                className="w-full group-hover/row:bg-primary-600 sm:w-8 sm:px-0 lg:w-full lg:px-4"
              >
                <span className="flex items-center sm:hidden lg:flex">
                  <span className="lg:hidden">{primaryButton.label}</span>
                  <span className="hidden lg:inline">Visit site</span>
                  {primaryButton.opensInNewTab && <ExternalIndicator rowGroup />}
                </span>
                <span className="hidden sm:flex lg:hidden">
                  <Icon name={primaryButton.icon} />
                  <span className="sr-only">{primaryButton.label}</span>
                </span>
              </Pill>
            </PrimaryButton>
          </>
        ) : (
          <PrimaryButton
            primaryButton={primaryButton}
            primaryDeal={primaryDeal}
            app={app}
            onClick={handleClick}
            className={cn(buttonClass, hideButton ? 'hidden sm:flex' : '')}
          >
            <div
              className={cn(
                'hidden sm:flex',
                inlineButtons &&
                  'lg:h-[30px] lg:w-[30px] lg:shrink-0 lg:items-center lg:justify-center lg:overflow-hidden lg:transition-[width,opacity] lg:duration-200 lg:ease-in-out lg:group-hover/row:w-0 lg:group-hover/row:opacity-0 lg:group-focus-visible:w-0 lg:group-focus-visible:opacity-0',
              )}
            >
              <Icon name={primaryButton.icon} />
            </div>
            {inlineButtons && (
              <div className="hidden grid-cols-[0fr] transition-[grid-template-columns] duration-200 ease-in-out group-hover/row:grid-cols-[1fr] group-focus-visible:grid-cols-[1fr] lg:grid">
                <span className="overflow-hidden whitespace-nowrap">
                  <span className="block px-3 text-14 font-medium">{primaryButton.label}</span>
                </span>
              </div>
            )}

            <div className={cn(tagClass, inlineButtons && 'sm:hidden md:hidden')}>
              <span className="relative z-20 flex items-center justify-center">
                {primaryButton.label}
              </span>
              {isPartner && <ExternalIndicator />}
            </div>
          </PrimaryButton>
        )}
      </div>
    </>
  )
}
